
import React from 'react';
import { ArrowLeft, MousePointerClick, ScrollText, Sparkles } from 'lucide-react';
import { Theme, Language } from '../../types';
import { translations } from '../../i18n';

interface LessonHeaderProps {
  onExit: () => void;
  viewMode: 'stepped' | 'scroll';
  setViewMode: (mode: 'stepped' | 'scroll') => void;
  progressPercent: number;
  isStreaming: boolean;
  autoPlay: boolean;
  setAutoPlay: (auto: boolean) => void;
  isDark: boolean;
  lang: Language;
}

export const LessonHeader: React.FC<LessonHeaderProps> = ({ 
  onExit, viewMode, setViewMode, progressPercent, isStreaming, autoPlay, setAutoPlay, isDark, lang 
}) => {
  const t = translations[lang];
  const navClass = isDark 
    ? "bg-slate-950/95 border-white/20 ring-white/10 text-white shadow-2xl" 
    : "bg-white/95 border-slate-300 ring-slate-200 text-slate-800 shadow-xl";

  return (
    <div className="pt-safe px-4 md:px-6 pb-2 sticky top-0 z-50">
      {/* Fallback for browsers that don't support env() in Tailwind directly or need extra breathing room */}
      <div style={{ paddingTop: 'env(safe-area-inset-top, 24px)' }}></div>
      
      <div className={`backdrop-blur-2xl border rounded-3xl p-3 flex items-center gap-4 ring-1 transition-all ${navClass}`}>
        <button 
          onClick={onExit} 
          className={`p-2.5 rounded-2xl transition-all shadow-sm ${isDark ? 'bg-white/10 hover:bg-white/20 text-white border border-white/10' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'}`}
        >
          <ArrowLeft size={22} strokeWidth={3} />
        </button>
        
        <div className={`flex rounded-xl p-1 border ${isDark ? 'bg-slate-800/80 border-white/10' : 'bg-slate-200/50 border-slate-300'}`}>
          <button 
            onClick={() => setViewMode('stepped')}
            className={`p-1.5 rounded-lg transition-all ${viewMode === 'stepped' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'}`}
          >
            <MousePointerClick size={18}/>
          </button>
          <button 
            onClick={() => setViewMode('scroll')}
            className={`p-1.5 rounded-lg transition-all ${viewMode === 'scroll' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'}`}
          >
            <ScrollText size={18}/>
          </button>
        </div>

        <div className={`flex-1 relative h-3 rounded-full overflow-hidden border ${isDark ? 'bg-slate-900 border-white/5' : 'bg-slate-300/50 border-slate-300'}`}>
          <div 
            className="h-full bg-gradient-to-r from-indigo-500 to-indigo-300 shadow-[0_0_15px_rgba(99,102,241,0.5)] transition-all duration-700 ease-out"
            style={{ width: `${progressPercent}%` }}
          ></div>
        </div>

        {isStreaming && (
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border animate-pulse ${isDark ? 'bg-indigo-500/20 border-indigo-500/30' : 'bg-indigo-100 border-indigo-300'}`}>
            <Sparkles size={14} className="text-indigo-600 dark:text-indigo-400" />
            <span className="hidden sm:inline text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">{t.thinking}</span>
          </div>
        )}

        {viewMode === 'stepped' && (
          <button 
            onClick={() => setAutoPlay(!autoPlay)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${autoPlay ? 'bg-emerald-500 text-white border-transparent shadow-lg' : 'bg-transparent text-slate-500 border-slate-300 dark:border-white/10'}`}
          >
            {autoPlay ? 'Sync ON' : 'Manual'}
          </button>
        )}
      </div>
    </div>
  );
};
